<template>
  <!--
    Carte de notification : le contenu d'un toast.

    Rendue par vue3-toastify, qui lui passe `data` (ce qu'on veut afficher) et
    `closeToast` (pour se fermer). Volontairement sans logique : c'est
    `utils/notify.js` qui décide quoi montrer, et `utils/network.js` quand.

    ATTENTION : vue3-toastify monte ses toasts dans une instance Vue à part,
    créée par ses soins. Aucun plugin de l'application n'y est installé — ni
    i18n, ni le store, ni le routeur. `$t`, `$store` et `$router` n'existent
    donc pas ici, et les y appeler faisait planter le rendu de la carte.
    Tout ce qui est traduit arrive déjà traduit, par `data`.
  -->
  <div class="sf-note" :class="`sf-note--${tone}`" role="alert" aria-live="polite">
    <span class="sf-note__icon">
      <!--
        Le cercle d'interdiction sur le signal wifi n'existe pas dans
        Bootstrap Icons — `bi-wifi-off` se contente d'un trait en travers.
        Il est donc dessiné ici. Le cercle est blanc, pas rouge : c'est le
        fond de la carte qui porte déjà le rouge de l'alerte.
      -->
      <svg
        v-if="icon === 'no-wifi'"
        class="sf-note__glyph"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <!--
          Deux ondes et un point, pas trois ondes : à 27 px, des traits assez
          épais pour se voir ne laissent pas la place à davantage. Trois ondes
          se touchaient et l'ensemble faisait une tache.
        -->
        <path d="M6.6 11.6a7 7 0 0 1 10.8 0" stroke-width="2.7" />
        <path d="M9.3 14.4a3.4 3.4 0 0 1 5.4 0" stroke-width="2.7" />
        <circle cx="12" cy="16.7" r="1.45" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="9.7" stroke-width="2.2" />
        <!--
          Coupure : un trait de la couleur du disque, posé sous la barre, détache
          celle-ci des ondes qu'elle traverse. Sans lui, tout étant blanc, la
          barre se fondait dans le signal. Il s'arrête avant le cercle, pour
          que la barre continue de s'y raccorder.
        -->
        <line
          x1="6.9"
          y1="17.1"
          x2="17.1"
          y2="6.9"
          stroke="var(--sf-note-disc)"
          stroke-width="3.3"
        />
        <line x1="5.14" y1="18.86" x2="18.86" y2="5.14" stroke-width="2.2" />
      </svg>
      <i v-else class="bi" :class="icon"></i>
    </span>

    <div class="sf-note__body">
      <p class="sf-note__title">{{ title }}</p>
      <p class="sf-note__text" v-if="text">{{ text }}</p>
      <p class="sf-note__hint" v-if="hint">
        <i class="bi bi-info-circle me-1"></i>{{ hint }}
      </p>
    </div>

    <button
      type="button"
      class="sf-note__close"
      :aria-label="closeLabel"
      @click.stop="close"
    >
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</template>

<script>
/* Icône par défaut de chaque ton, qu'un appelant peut remplacer — la carte
   « pas de connexion » parle mieux avec un wifi barré qu'avec un triangle. */
const DEFAULT_ICONS = {
  success: "bi-check-circle-fill",
  error: "bi-x-octagon-fill",
  warning: "bi-exclamation-triangle-fill",
  info: "bi-info-circle-fill",
};

export default {
  name: "notification-card",
  props: {
    /** Contenu, transmis par vue3-toastify via l'option `data`. */
    data: { type: Object, default: () => ({}) },
    /** Fermeture du toast, fournie par vue3-toastify. */
    closeToast: { type: Function, default: null },
  },
  computed: {
    tone() {
      return this.data.tone || "info";
    },
    icon() {
      return this.data.icon || DEFAULT_ICONS[this.tone] || DEFAULT_ICONS.info;
    },
    title() {
      return this.data.title || "";
    },
    text() {
      return this.data.text || "";
    },
    hint() {
      return this.data.hint || "";
    },
    /* Traduit en amont : voir la remarque en tête de fichier. */
    closeLabel() {
      return this.data.closeLabel || "Fermer";
    },
  },
  methods: {
    close() {
      if (this.closeToast) this.closeToast();
    },
  },
};
</script>

<style scoped>
/*
  Carte en aplat de couleur, texte blanc.

  Une notification réseau doit arracher le regard à ce qu'on était en train de
  faire : un fond franc y parvient là où un liséré coloré se noie dans la
  page. Le rouge de la coupure est celui d'une alerte de danger, pas d'une
  mise en garde.

  Les teintes sont choisies pour que du texte blanc y reste lisible : chacune
  dépasse le rapport de contraste 4,5:1 exigé pour du texte courant. C'est
  aussi pourquoi elles sont plus sourdes que les couleurs d'accent utilisées
  ailleurs dans l'application — `#f5365c`, par exemple, ne plafonne qu'à 3,8:1
  et rendrait le message pâteux.

  Un seul jeu de couleurs pour les deux thèmes : un aplat saturé ressort
  aussi bien sur fond clair que sur fond sombre, et il n'y a qu'une
  apparence à maintenir.
*/
.sf-note {
  --sf-note-from: #4553c8;
  --sf-note-to: #3a46ac;
  --sf-note-glow: rgba(69, 83, 200, 0.5);
  /* Fond du disque de l'icône. Sert aussi de « gomme » au pictogramme :
     c'est parce que les deux partagent cette couleur que la coupure sous la
     barre reste invisible. */
  --sf-note-disc: #6974c9;

  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 18px;
  border-radius: 14px;
  background: linear-gradient(125deg, var(--sf-note-from), var(--sf-note-to));
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  /* L'ombre prend la couleur de la carte : elle la pose sur la page au lieu
     de la découper au couteau. */
  box-shadow: 0 18px 44px -14px var(--sf-note-glow), 0 2px 8px -2px rgba(9, 14, 28, 0.35);
}

.sf-note--error {
  --sf-note-from: #e0213f;
  --sf-note-to: #c2102f;
  --sf-note-glow: rgba(224, 33, 63, 0.55);
  --sf-note-disc: #db4b63;
}

.sf-note--success {
  --sf-note-from: #12897e;
  --sf-note-to: #0f6d64;
  --sf-note-glow: rgba(18, 137, 126, 0.5);
  --sf-note-disc: #459890;
}

.sf-note--warning {
  --sf-note-from: #c25f08;
  --sf-note-to: #a34d06;
  --sf-note-glow: rgba(194, 95, 8, 0.5);
  --sf-note-disc: #c47b3d;
}

.sf-note__icon {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  background: var(--sf-note-disc);
  color: #ffffff;
}

.sf-note__glyph {
  width: 30px;
  height: 30px;
}

.sf-note__body {
  flex: 1 1 auto;
  min-width: 0;
}

.sf-note__title {
  margin: 0;
  font-size: 17.5px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.2px;
}

.sf-note__text {
  margin: 6px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  /* Blanc légèrement voilé : le titre garde la première place sans que le
     message devienne difficile à lire. */
  color: rgba(255, 255, 255, 0.92);
  /* Un message serveur peut être long : il s'enroule au lieu de déborder. */
  overflow-wrap: anywhere;
}

.sf-note__hint {
  margin: 9px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.78);
}

.sf-note__close {
  flex: 0 0 auto;
  background: transparent;
  border: none;
  color: #ffffff;
  opacity: 0.7;
  font-size: 12px;
  line-height: 1;
  padding: 2px 4px;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.sf-note__close:hover {
  opacity: 1;
}
</style>

<style>
/*
  Réglages de vue3-toastify pour ces cartes.

  Non scopé : ces classes appartiennent à la bibliothèque, qui rend le toast
  hors de ce composant. Le fichier étant importé par `utils/notify.js`, les
  règles sont en place dès la première notification.
*/

/* Les cartes sont plus larges que les 320 px par défaut — deux lignes de
   texte y tiennent sans être hachées. La variable est celle de la
   bibliothèque : tous les toasts de l'application en profitent. */
:root {
  --toastify-toast-width: min(420px, calc(100vw - 32px));
  --toastify-toast-min-height: 0;
}

/* La carte peint son propre fond : l'habillage de la bibliothèque disparaît
   pour ne pas doubler les bords ni les ombres. */
.sf-note-toast.Toastify__toast {
  padding: 0;
  margin-bottom: 12px;
  min-height: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 14px;
  cursor: default;
  overflow: visible;
}

.sf-note-toast .Toastify__toast-body {
  padding: 0;
  margin: 0;
}

/* La barre de progression revient à l'intérieur de la carte, aux couleurs
   du ton — sinon elle flotte sous un fond transparent. */
.sf-note-toast .Toastify__progress-bar {
  height: 3px;
  bottom: 0;
  left: 0;
  width: 100%;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  /* Blanche : la couleur que la bibliothèque lui donnerait selon le type
     serait celle de la carte, sur laquelle elle serait invisible. */
  background: rgba(255, 255, 255, 0.65);
  opacity: 1;
}

/*
  Entrée et sortie par la droite.

  Sélecteurs doublés (`.Toastify__toast.sf-note-*`) pour passer devant
  `.Toastify--animate`, qui impose sinon sa durée de 0,7 s. La courbe d'entrée
  décélère longuement : la carte arrive vite puis se pose, au lieu de
  s'arrêter net.
*/
@keyframes sfNoteIn {
  from {
    opacity: 0;
    transform: translate3d(115%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translateZ(0);
  }
}

@keyframes sfNoteOut {
  from {
    opacity: 1;
    transform: translateZ(0);
  }
  to {
    opacity: 0;
    transform: translate3d(115%, 0, 0);
  }
}

.Toastify__toast.sf-note-enter {
  animation: sfNoteIn 0.52s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.Toastify__toast.sf-note-exit {
  animation: sfNoteOut 0.3s cubic-bezier(0.55, 0, 0.68, 0.53) both;
}

/* Écrans étroits : la carte prend la largeur disponible, marges comprises. */
@media only screen and (max-width: 480px) {
  .Toastify__toast-container {
    padding: 8px;
    width: 100vw;
  }

  .sf-note-toast.Toastify__toast {
    margin-bottom: 8px;
    border-radius: 14px;
  }
}
</style>
